import React, { useMemo } from "react";

import { To } from "react-router-dom";

import { Skeleton, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import styled from "@mui/material/styles/styled";

import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import useBanners from "@app/analytics/hooks/useBanners";
import * as Events from "@app/analytics/tracking/events";
import useAuth from "@app/common/auth/useAuth";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import ROUTES from "@app/common/routes";
import type { UserSettings } from "@app/common/types";
import * as UserSettingsUtils from "@app/common/utils/userSettingsUtils";
import * as UserUtils from "@app/common/utils/userUtils";

import AlertBanner from "@rui/AlertBanner";
import Link from "@rui/Link";
import { Column, Row } from "@rui/Stack";

const CALENDAR_PERMISSION_LEVEL = {
  all: "All meetings automatically",
  hosting_only: "Only meetings I host",
  invite_only: "Only if invited",
};

const getRecapLabel = (settings: UserSettings | null) => {
  const emailRecapSettings =
    UserSettingsUtils.getEnabledUserDistributionSettingForCategoryAndProvider(
      settings,
      { category: "meeting_report", provider: "email" }
    );

  if (!emailRecapSettings) return "Nobody";

  switch (emailRecapSettings.sendTo) {
    case "participants_with_access":
      return "Everyone invited";
    case "me":
      return "Just me";
    default:
      return "Nobody";
  }
};

const Greeting = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "1rem",
  fontWeight: 500,
  lineHeight: "1.25rem",
  color: theme.palette.shades.neutral[800],
}));

const SettingLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "0.75rem",
  fontWeight: 500,
  lineHeight: "1.125rem",
  color: theme.palette.shades.neutral[800],
}));

const SettingLink = styled(Link)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  fontWeight: 400,
  color: theme.palette.shades.primary[500],
  textDecoration: "underline",
}));

const SettingItem = ({
  label,
  value,
  to,
  loading = false,
  onClick,
}: {
  label: string;
  value: string;
  to: To;
  loading?: boolean;
  onClick?: () => void;
}) => {
  return (
    <SettingLabel noWrap>
      {label}:{" "}
      {loading ? (
        <Skeleton
          variant="text"
          height="1.125rem"
          sx={{ display: "inline-block", minWidth: "60px" }}
        />
      ) : (
        <SettingLink to={to} onClick={onClick}>
          {value}
        </SettingLink>
      )}
    </SettingLabel>
  );
};

const FirstTimeSettingsBanner = ({
  priority,
  onDismiss,
}: {
  priority: number;
  onDismiss: () => void;
}) => {
  const { user } = useAuth();

  const isEligible = useMemo(
    () => UserUtils.eligibleForFirstTimeSettingsBanner(user),
    [user]
  );

  const {
    hasConnectedCalendarProviders,
    connectedCalendarProviders,
    isLoading,
  } = useConnectedProviders({
    enabled: isEligible,
  });

  const shouldShow = isEligible && hasConnectedCalendarProviders;
  const handleClick = (setting: string) =>
    Events.firstTimeSettingsClick({ setting });

  const { show } = useBanners({
    id: "firstTimeSettings",
    priority,
    shouldShow,
  });

  if (!show) return null;

  return (
    <AlertBanner
      icon={false}
      severity="info"
      sx={{ backgroundColor: "shades.primary.100" }}
      onClose={onDismiss}
      action={
        <Button
          sx={{ height: "40px", whiteSpace: "nowrap", mr: 1.5 }}
          component={Link}
          variant="contained"
          href={ROUTES.SETTINGS_ROOT}
          onClick={() => handleClick("review")}
        >
          Review Settings
        </Button>
      }
    >
      <Column spacing={0.5} pl={0.5}>
        <Greeting>
          Welcome to Read{user?.firstName ? `, ${user.firstName}` : ""}! Please
          review your account settings:
        </Greeting>
        <Row variant="left" columnGap={2} flexWrap="wrap">
          <SettingItem
            label="Calendar"
            value={`${connectedCalendarProviders.length} connected`}
            to={ROUTES.USER_SETTINGS_INTEGRATIONS}
            loading={isLoading}
            onClick={() => handleClick("calendar")}
          />
          <SettingItem
            label="Read will join"
            value={
              CALENDAR_PERMISSION_LEVEL[user?.calendarPermissionLevel ?? "all"]
            }
            to={ROUTES.USER_SETTINGS_JOIN_PREFERENCES}
            loading={!user}
            onClick={() => handleClick("calendarPermissionLevel")}
          />
          <SettingItem
            label="Send recaps via email to"
            value={getRecapLabel(user)}
            to={ROUTES.USER_SETTINGS_REPORTING}
            loading={!user}
            onClick={() => handleClick("sendMeetingRecap")}
          />
        </Row>
      </Column>
    </AlertBanner>
  );
};

const FirstTimeSettingsBannerGate = ({ priority }: { priority: number }) => {
  const [dismissed, setDismissed] = useLocalStorage(
    "firstTimeSettingsBannerDismissed",
    false
  );

  if (dismissed) return null;

  const handleDismiss = () => setDismissed(true);

  return (
    <FirstTimeSettingsBanner priority={priority} onDismiss={handleDismiss} />
  );
};

export default FirstTimeSettingsBannerGate;
